import { Message, ConversationObj, TaskWithDialogue } from './types';
import { ConversationDialogueState } from './DialogueManagementConversation/ConversationDialogueBlueprint';
import { textToSpeechService } from '../../../services/textToSpeech';

export class Conversation {
  id: string;
  messages: Message[];
  tasks: TaskWithDialogue[];
  conversationDialogueState: ConversationDialogueState;
  isProcessingConversationDialogue: boolean;
  isSubmitted: boolean;
  isCancelled: boolean;
  abortConfirmationPending: boolean;
  abortReturnState?: ConversationDialogueState;
  abortReturnMessage?: { text: string; textKey?: string };
  time: Date;
  savedConversationId?: number | null; // SQL conversations.id after first save (for PATCH updates)
  assignementId?: number | null;
  assignementAddress?: string | null;

  constructor(id: string) {
    this.id = id;
    this.messages = [];
    this.tasks = [];
    this.conversationDialogueState = 'conv_welcome';
    this.isProcessingConversationDialogue = false;
    this.isSubmitted = false;
    this.isCancelled = false;
    this.abortConfirmationPending = false;
    this.time = new Date();
    this.assignementId = undefined;
    this.assignementAddress = undefined;
  }

  addUserMessage(text: string): void {
    const message: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: text,
      sender: 'User',
      timestamp: new Date(),
      isAssistant: false,
      messageType: 'user'
    };
    this.messages.push(message);
  }

  addSystemMessage(text: string, textKey?: string): void {
    // Check if the last message is identical to prevent duplicates
    const lastMessage = this.messages[this.messages.length - 1];
    if (lastMessage && 
        lastMessage.isAssistant && 
        lastMessage.text === text && 
        lastMessage.textKey === textKey) {
      console.log("🔄 Duplicate message detected, skipping:", text);
      return; // Don't add duplicate message
    }

    const message: Message = {
      id: Math.random().toString(36).substr(2, 9),
      text: text,
      textKey: textKey,
      sender: 'AI Assistant',
      timestamp: new Date(),
      isAssistant: true,
      messageType: 'assistant'
    };
    this.messages.push(message);

    // Play audio if textKey is provided and text-to-speech is enabled
    if (textKey) {
      textToSpeechService.playAudio(textKey);
    }
  }

  addTask(task: TaskWithDialogue): void {
    this.tasks.push(task);
  }

  updateDialogueState(newState: ConversationDialogueState): void {
    this.conversationDialogueState = newState;
  }

  setProcessingDialogue(isProcessing: boolean): void {
    this.isProcessingConversationDialogue = isProcessing;
  }

  markAsSubmitted(): void {
    this.isSubmitted = true;
  }

  markAsCancelled(): void {
    this.isCancelled = true;
  }

  startAbortConfirmation(currentState: ConversationDialogueState): void {
    this.abortConfirmationPending = true;
    this.abortReturnState = currentState;
    // Capture the last assistant message to reprint if user cancels the abort
    const lastAssistantMessage = [...this.messages].reverse().find((message) => message.isAssistant);
    if (lastAssistantMessage) {
      this.abortReturnMessage = {
        text: lastAssistantMessage.text || '',
        textKey: lastAssistantMessage.textKey,
      };
    } else {
      this.abortReturnMessage = undefined;
    }
  }

  clearAbortConfirmation(): void {
    this.abortConfirmationPending = false;
    this.abortReturnState = undefined;
    this.abortReturnMessage = undefined;
  }

  findTask(taskId: string) {
    return this.tasks.find((task: any) => task.id === taskId);
  }

  updateTask(taskId: string, updates: Partial<TaskWithDialogue>): void {
    const taskIndex = this.tasks.findIndex(task => task.id === taskId);
    if (taskIndex !== -1) {
      this.tasks[taskIndex] = { ...this.tasks[taskIndex], ...updates };
    }
  }

  export(): ConversationObj {
    return {
      id: this.id,
      time: this.time,
      messages: this.messages,
      tasks: this.tasks,
      conversationDialogueState: this.conversationDialogueState,
      isProcessingConversationDialogue: this.isProcessingConversationDialogue,
      isSubmitted: this.isSubmitted,
      isCancelled: this.isCancelled,
      savedConversationId: this.savedConversationId,
      abortConfirmationPending: this.abortConfirmationPending,
      abortReturnState: this.abortReturnState,
      abortReturnMessage: this.abortReturnMessage,
      assignementId: this.assignementId,
      assignementAddress: this.assignementAddress,
    };
  }

  static fromConversationObj(conversationObj: ConversationObj): Conversation {
    const conversation = new Conversation(conversationObj.id);
    conversation.messages = conversationObj.messages;
    conversation.tasks = conversationObj.tasks;
    conversation.conversationDialogueState = conversationObj.conversationDialogueState || 'conv_welcome';
    conversation.isProcessingConversationDialogue = conversationObj.isProcessingConversationDialogue || false;
    conversation.isSubmitted = conversationObj.isSubmitted || false;
    conversation.isCancelled = conversationObj.isCancelled || false;
    conversation.savedConversationId = conversationObj.savedConversationId ?? undefined;
    conversation.abortConfirmationPending = conversationObj.abortConfirmationPending || false;
    conversation.abortReturnState = conversationObj.abortReturnState;
    conversation.abortReturnMessage = conversationObj.abortReturnMessage;
    conversation.time = conversationObj.time;
    conversation.assignementId = conversationObj.assignementId;
    conversation.assignementAddress = conversationObj.assignementAddress;
    return conversation;
  }
} 